import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Users, FileText, Download, Upload, KeyRound,
  UserPlus, Loader2, Trash2, Search, Building2, Eye,
  ChevronLeft, ChevronRight, Image as ImageIconLucide,
} from "lucide-react";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fileUrl } from "@/components/ImageUpload";
import Logo from "@/components/Logo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import formatDoctorName from "@/lib/names";

const STATUSES = ["Pending", "Scheduled", "Decorated"];

// Entries made up to the end of 31 Oct 2026 count as in time; from 1 Nov onwards
// they are late. Fixed to IST so the verdict does not move with the viewer's clock.
const IST = "Asia/Kolkata";
const ENTRY_CUTOFF = new Date("2026-11-01T00:00:00+05:30");
const ENTRY_FILTERS = { all: "All entries", "in-time": "In time", late: "Late entries" };

const isLateEntry = (s) => {
  const d = new Date(s.created_at);
  return !Number.isNaN(d.getTime()) && d >= ENTRY_CUTOFF;
};

const formatSubmittedAt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZone: IST,
  });
};
const STANDEE_STATUSES = ["None", "Image Uploaded", "Standee Downloaded"];
const STANDEE_STYLES = {
  None: "bg-slate-100 text-slate-500",
  "Image Uploaded": "bg-blue-50 text-blue-600",
  "Standee Downloaded": "bg-emerald-50 text-emerald-600",
};
const STATUS_STYLES = {
  Pending: "text-amber-600",
  Scheduled: "text-blue-600",
  Decorated: "text-emerald-600",
};

function Stat({ icon, label, value, color }) {
  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">{label}</span>
        <div className="h-9 w-9 rounded-lg grid place-items-center" style={{ background: `${color}15` }}>
          {icon}
        </div>
      </div>
      <p className="font-heading text-4xl font-black tracking-tighter text-slate-900 mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [stats, setStats] = useState({ total_mrs: 0, total_submissions: 0, total_headquarters: 0 });
  const [subs, setSubs] = useState([]);
  const [mrs, setMrs] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [standeeFilter, setStandeeFilter] = useState("all");
  const [entryFilter, setEntryFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [newMr, setNewMr] = useState({ emp_code: "", name: "", headquarter: "", mobile: "" });
  const [busy, setBusy] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/submissions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/mrs").then((r) => setMrs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // A narrowed result set is shorter, so start it from the top.
  useEffect(() => { setPage(1); }, [q, perPage, standeeFilter, entryFilter]);

  const changePassword = async () => {
    if (pwd.new_password !== pwd.confirm) {
      toast.error("The new passwords do not match.");
      return;
    }
    setPwdBusy(true);
    try {
      await api.post("/admin/password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password updated. Use it the next time you sign in.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setPwdOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the password");
    } finally {
      setPwdBusy(false);
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const addMr = async () => {
    if (!newMr.emp_code || !newMr.name) { toast.error("EMP Code and Name are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/mrs", newMr);
      toast.success(`Manager ${newMr.emp_code} added`);
      setNewMr({ emp_code: "", name: "", headquarter: "", mobile: "" });
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mrs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} managers added, ${data.skipped} skipped (duplicates)`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteMr = async (id, code) => {
    if (!window.confirm(`Remove manager ${code}?`)) return;
    await api.delete(`/admin/mrs/${id}`);
    toast.success("Manager removed");
    load();
  };

  const changeStatus = async (id, status) => {
    setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    try {
      await api.patch(`/admin/submissions/${id}/status`, { status });
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Could not update status");
      load();
    }
  };

  // Hitting the URL directly keeps the browser's own download UI, and the server
  // marks it downloaded in the same request.
  const downloadStandee = (subId) => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${subId}/standee/download?auth=${token}`, "_blank");
    setTimeout(load, 1500);
  };

  // The lama is rendered from the lama studio's settings, else the standee's.
  const downloadLama = (subId) => {
    const token = localStorage.getItem("bc_token");
    window.open(`${API}/admin/submissions/${subId}/lama?auth=${token}`, "_blank");
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/submissions/export", { responseType: "blob" });
      saveAs(new Blob([res.data]), "clinic_submissions.csv");
    } catch {
      toast.error("Export failed");
    }
  };

  // Searches fields that are no longer columns (qualification, requirements, address)
  // too, so a row stays findable by anything on its detail page.
  const filtered = subs.filter((s) => {
    const st = s.standee_status || "None";
    if (standeeFilter !== "all" && st !== standeeFilter) return false;
    if (entryFilter !== "all" && (entryFilter === "late") !== isLateEntry(s)) return false;
    const t = q.trim().toLowerCase();
    return !t || [
      s.doctor_name, s.mr_emp_code, s.mr_name, s.headquarter, s.manager_name,
      s.manager_mobile, s.qualification, s.additional_requirements,
      s.status, s.standee_status,
    ].some((v) => (v || "").toLowerCase().includes(t));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-heading font-bold text-slate-900 leading-none">Admin Panel</p>
              <p className="text-xs text-slate-500">Blue Cross · World Diabetes Day</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={pwdOpen} onOpenChange={(o) => { setPwdOpen(o); if (!o) setPwd({ current_password: "", new_password: "", confirm: "" }); }}>
              <DialogTrigger asChild>
                <Button data-testid="admin-password-open" variant="outline" size="sm" className="rounded-lg h-11 px-3">
                  <KeyRound className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Change Password</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Change Admin Password</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <Input data-testid="admin-current-password" type="password" autoComplete="current-password"
                      value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} placeholder="••••••••" />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input data-testid="admin-new-password" type="password" autoComplete="new-password"
                      value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} placeholder="At least 8 characters" />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <Input data-testid="admin-confirm-password" type="password" autoComplete="new-password"
                      value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} placeholder="Repeat the new password" />
                  </div>
                  <p className="text-xs text-slate-500">
                    Only the admin login changes. Managers keep signing in with their EMP Code.
                  </p>
                </div>
                <DialogFooter>
                  <Button data-testid="admin-password-save" onClick={changePassword}
                    disabled={pwdBusy || !pwd.current_password || !pwd.new_password || !pwd.confirm}
                    className="bg-[#0B5351] hover:bg-[#094241]">
                    {pwdBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button data-testid="admin-logout" onClick={doLogout} variant="outline" size="sm" className="rounded-lg h-11 px-3">
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat icon={<Users className="h-5 w-5 text-[#0B5351]" />} label="Total Managers" value={stats.total_mrs} color="#0B5351" />
          <Stat icon={<FileText className="h-5 w-5 text-[#E63946]" />} label="Submissions" value={stats.total_submissions} color="#E63946" />
          <Stat icon={<Building2 className="h-5 w-5 text-[#F59E0B]" />} label="Headquarters" value={stats.total_headquarters} color="#F59E0B" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex p-1 rounded-xl bg-slate-100 border border-slate-200">
            <button data-testid="admin-tab-submissions" onClick={() => setTab("submissions")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "submissions" ? "bg-white text-[#0B5351] shadow-sm" : "text-slate-500"}`}>
              Submissions
            </button>
            <button data-testid="admin-tab-mrs" onClick={() => setTab("mrs")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "mrs" ? "bg-white text-[#0B5351] shadow-sm" : "text-slate-500"}`}>
              Managers
            </button>
          </div>

          {tab === "submissions" ? (
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search doctor, EMP code, manager, HQ…"
                  className="h-10 pl-9 w-56 sm:w-72 bg-white" />
              </div>
              <Select value={standeeFilter} onValueChange={setStandeeFilter}>
                <SelectTrigger data-testid="standee-filter" className="h-10 w-[190px] bg-white">
                  <SelectValue placeholder="All standees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="standee-filter-all">All standees</SelectItem>
                  {STANDEE_STATUSES.map((st) => (
                    <SelectItem key={st} value={st} data-testid={`standee-filter-${st.replace(/ /g, "-")}`}>{st}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={entryFilter} onValueChange={setEntryFilter}>
                <SelectTrigger data-testid="entry-filter" className="h-10 w-[160px] bg-white">
                  <SelectValue placeholder="All entries" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ENTRY_FILTERS).map(([value, label]) => (
                    <SelectItem key={value} value={value} data-testid={`entry-filter-${value}`}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="export-csv" onClick={exportCsv} className="h-10 rounded-lg bg-[#0B5351] hover:bg-[#094241]">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onUpload} className="hidden" data-testid="upload-input" />
              <Button data-testid="upload-mrs" onClick={() => fileRef.current?.click()} disabled={busy} variant="outline" className="h-10 rounded-lg">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} Upload CSV/Excel
              </Button>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-mr-open" className="h-10 rounded-lg bg-[#0B5351] hover:bg-[#094241]">
                    <UserPlus className="h-4 w-4 mr-2" /> Add Manager
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Manager</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2">
                      <Label>EMP Code *</Label>
                      <Input data-testid="new-mr-empcode" value={newMr.emp_code} onChange={(e) => setNewMr({ ...newMr, emp_code: e.target.value })} placeholder="EMP1024" />
                      <p className="text-xs text-slate-500">This becomes the manager's login username & password.</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input data-testid="new-mr-name" value={newMr.name} onChange={(e) => setNewMr({ ...newMr, name: e.target.value })} placeholder="Full name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Headquarter</Label>
                        <Input data-testid="new-mr-hq" value={newMr.headquarter} onChange={(e) => setNewMr({ ...newMr, headquarter: e.target.value })} placeholder="Mumbai" />
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile</Label>
                        <Input data-testid="new-mr-mobile" value={newMr.mobile} onChange={(e) => setNewMr({ ...newMr, mobile: e.target.value })} placeholder="Number" />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button data-testid="new-mr-save" onClick={addMr} disabled={busy} className="bg-[#0B5351] hover:bg-[#094241]">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Manager"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {tab === "submissions" ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="submissions-table">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>EMP Code</TableHead>
                    <TableHead>Doctor</TableHead>
                    <TableHead>Headquarter</TableHead>
                    <TableHead>Manager</TableHead>
                    <TableHead>Doctor Photo</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Entry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Standee</TableHead>
                    <TableHead>Lama</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-12 text-slate-400">No submissions found.</TableCell></TableRow>
                  ) : pageRows.map((s, i) => (
                    <TableRow key={s.id} className={i % 2 ? "bg-slate-50/50" : ""}>
                      <TableCell className="font-mono text-xs font-semibold text-[#0B5351]">{s.mr_emp_code}</TableCell>
                      <TableCell className="font-medium">{formatDoctorName(s.doctor_name)}</TableCell>
                      <TableCell>{s.headquarter}</TableCell>
                      <TableCell><div className="text-sm">{s.manager_name}<br /><span className="text-slate-400 text-xs">{s.manager_mobile}</span></div></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {s.doctor_photo ? (
                            <a href={fileUrl(s.doctor_photo)} target="_blank" rel="noreferrer" title="Doctor photo" data-testid={`photo-doctor-${s.id}`}>
                              <img src={fileUrl(s.doctor_photo)} alt="doctor" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} className="h-10 w-10 rounded-lg object-cover border border-slate-200 hover:ring-2 hover:ring-[#0B5351] transition-all" />
                            </a>
                          ) : null}
                          {!s.doctor_photo ? <span className="text-slate-300">—</span> : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap" data-testid={`submitted-at-${s.id}`}>
                        {formatSubmittedAt(s.created_at)}
                      </TableCell>
                      <TableCell>
                        <span data-testid={`entry-status-${s.id}`}
                          className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap border ${
                            isLateEntry(s)
                              ? "bg-red-50 text-[#E63946] border-red-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {isLateEntry(s) ? "Late" : "In Time"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select value={s.status || "Pending"} onValueChange={(v) => changeStatus(s.id, v)}>
                          <SelectTrigger data-testid={`status-select-${s.id}`} className={`h-9 w-[130px] font-semibold ${STATUS_STYLES[s.status] || STATUS_STYLES.Pending}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((st) => (
                              <SelectItem key={st} value={st} data-testid={`status-option-${st}`}>{st}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${STANDEE_STYLES[s.standee_status] || STANDEE_STYLES.None}`}
                            data-testid={`standee-status-${s.id}`}>
                            {s.standee_status || "None"}
                          </span>
                          <Button variant="outline" size="sm" data-testid={`standee-open-${s.id}`}
                            title="Open standee studio"
                            onClick={() => navigate(`/admin/submissions/${s.id}/standee`)} className="rounded-lg h-9 px-2.5 whitespace-nowrap">
                            <ImageIconLucide className="h-4 w-4" />
                          </Button>
                          {(s.standee_file || s.doctor_photo) && (
                            <Button variant="outline" size="sm" data-testid={`standee-download-${s.id}`}
                              title={s.standee_file ? "Download standee" : "Download default standee"}
                              onClick={() => downloadStandee(s.id)}
                              className="rounded-lg h-9 px-2.5 whitespace-nowrap border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" data-testid={`lama-open-${s.id}`}
                            title="Open lama studio"
                            onClick={() => navigate(`/admin/submissions/${s.id}/lama-studio`)} className="rounded-lg h-9 px-2.5 whitespace-nowrap">
                            <ImageIconLucide className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" data-testid={`lama-download-${s.id}`}
                            title={s.lama_photo || s.standee_photo || s.doctor_photo ? "Download lama" : "No doctor photo; upload one in the lama studio"}
                            disabled={!s.lama_photo && !s.standee_photo && !s.doctor_photo}
                            onClick={() => downloadLama(s.id)}
                            className="rounded-lg h-9 px-2.5 whitespace-nowrap border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" data-testid={`view-submission-${s.id}`}
                          onClick={() => navigate(`/admin/submissions/${s.id}`)}
                          className="rounded-lg h-9 px-3 whitespace-nowrap">
                          <Eye className="h-4 w-4 mr-1.5" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-slate-200 bg-slate-50/60">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span data-testid="pagination-summary">
                  {filtered.length === 0
                    ? "No results"
                    : `${(currentPage - 1) * perPage + 1}–${Math.min(currentPage * perPage, filtered.length)} of ${filtered.length}`}
                </span>
                <Select value={String(perPage)} onValueChange={(v) => setPerPage(Number(v))}>
                  <SelectTrigger data-testid="per-page-select" className="h-9 w-[110px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`per-page-${n}`}>{n} / page</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" data-testid="page-prev"
                  disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}
                  className="rounded-lg h-9 px-3">
                  <ChevronLeft className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Prev</span>
                </Button>
                <span className="text-sm text-slate-600 tabular-nums px-1" data-testid="page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <Button variant="outline" size="sm" data-testid="page-next"
                  disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}
                  className="rounded-lg h-9 px-3">
                  <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4 sm:ml-1" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="mrs-table">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>EMP Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Headquarter</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mrs.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">No managers yet. Add one or upload a file.</TableCell></TableRow>
                  ) : mrs.map((m, i) => (
                    <TableRow key={m.id} className={i % 2 ? "bg-slate-50/50" : ""}>
                      <TableCell className="font-mono text-xs font-semibold text-[#0B5351]">{m.emp_code}</TableCell>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{m.headquarter || "—"}</TableCell>
                      <TableCell className="text-slate-500">{m.mobile || "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button data-testid={`delete-mr-${m.emp_code}`} onClick={() => deleteMr(m.id, m.emp_code)} variant="ghost" size="sm" className="text-slate-400 hover:text-[#E63946]">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
